import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

interface MobilePageHeaderProps {
    title: string;
    subtitle?: string;
    icon?: string; // Material symbol name
    showBackButton?: boolean;
    onBack?: () => void;
    rightElement?: React.ReactNode;
}

/**
 * Composant d'en-tête premium pour l'application mobile native.
 * Utilise le glassmorphism et des dégradés pour un rendu moderne.
 */
export const MobilePageHeader: React.FC<MobilePageHeaderProps> = ({
    title,
    subtitle,
    icon,
    showBackButton = false,
    onBack,
    rightElement
}) => {
    const navigate = useNavigate();
    const isNative = Capacitor.isNativePlatform();

    // Si on n'est pas sur mobile natif, ce composant peut être masqué ou rendu différemment.
    // Mais selon le plan, il est spécifiquement appelé dans des blocs conditionnels isNative.
    
    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate(-1);
        }
    };

    return (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-[#0d1117]/80 backdrop-blur-xl border-b border-white/5 px-4 pt-10 pb-3 shadow-lg shadow-black/20 animate-in fade-in slide-in-from-top duration-500">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-1 min-w-0 gap-2">
                    <div className="flex items-center gap-3">
                        {showBackButton && (
                            <button
                                onClick={handleBack}
                                className="p-2 -ml-2 text-zinc-400 hover:text-white transition-colors active:scale-90"
                                aria-label="Retour"
                            >
                                <ChevronLeft size={20} />
                            </button>
                        )}
                        <div className="flex items-center gap-2.5 min-w-0">
                            {icon && (
                                <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center shadow-lg shadow-primary/10 shrink-0">
                                    <span className="material-symbols-outlined text-primary text-xl" translate="no">{icon}</span>
                                </div>
                            )}
                            <h1 className="text-lg font-bold bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent tracking-tight uppercase truncate">
                                {title}
                            </h1>
                        </div>
                    </div>
                    {rightElement && (
                        <div className="flex items-center">
                            {rightElement}
                        </div>
                    )}
                </div>
                {subtitle && (
                    <p className="text-zinc-500 text-[11px] leading-tight pl-0.5 animate-in fade-in slide-in-from-left duration-700 delay-200 line-clamp-1">
                        {subtitle}
                    </p>
                )}
            </div>
        </div>
    );
};

